import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Connects to data-controller="time-activity-modal"
export default class extends Controller {
  static targets = ["customerId", "itemId", "projectId", "planId", "duration", "description", "submit"]
  static outlets = ["autocomplete"]
  // true only for a brand-new entry. The sequential keyboard focus flow
  // (auto-advance between fields, Enter-in-description → Save) is a
  // new-entry convenience; when editing, the user is deliberately changing a
  // single field, so we leave focus where it is.
  static values = { new: Boolean }

  connect() {
    // Only auto-focus customer field on new entries (no customer selected yet)
    const customerAutocomplete = this.findAutocompleteOutletById("time_activity_customer_id_wrapper")
    if (customerAutocomplete && !customerAutocomplete.hiddenTarget.value) {
      customerAutocomplete.setFocus()
    }
  }

  findAutocompleteOutletById(id) {
    return this.autocompleteOutlets.find(outlet =>
      outlet.element.id === id
    )
  }

  setFocusToCustomer() {
    const customerAutocomplete = this.findAutocompleteOutletById("time_activity_customer_id_wrapper")
    if (customerAutocomplete)
      customerAutocomplete.setFocus()
  }

  setFocusToItem() {
    this.focusAutocompleteById("time_activity_item_id_wrapper")
  }

  focusAutocompleteById(id) {
    const ac = this.findAutocompleteOutletById(id)
    if (ac) {
      ac.setFocus()
      return true
    }
    return false
  }

  // Like focusAutocompleteById, but declines a field that already has a value —
  // same hiddenTarget check the item default uses. Lets focus fall through to
  // the description when the plan arrived pre-selected (Plan#default_selected)
  // instead of parking the cursor on a question already answered.
  focusUnfilledAutocompleteById(id) {
    const ac = this.findAutocompleteOutletById(id)
    if (!ac || ac.hiddenTarget.value) return false

    ac.setFocus()
    return true
  }

  setFocusToDescription() {
    if (this.hasDescriptionTarget) {
      this.descriptionTarget.focus()
      return true
    }
    return false
  }

  // Plain Enter in the description advances to the Save button (where a
  // second Enter submits). Shift+Enter still inserts a newline.
  descriptionEnter(event) {
    if (!this.newValue) return
    if (event.shiftKey) return
    event.preventDefault()
    if (this.hasSubmitTarget) this.submitTarget.focus()
  }

  update(event) {
    switch (event.target.name) {
      case 'time_activity[customer_id]':
        // Always reload the dependent fields; focus is handled (new-only) there.
        this.loadCustomerFields(event.target.value)
        break
      case 'time_activity[item_id]':
        if (this.newValue) this.advancePastItem()
        break
      case 'time_activity[project_id]':
        if (this.newValue) {
          this.focusUnfilledAutocompleteById("time_activity_plan_id_wrapper")
            || this.setFocusToDescription()
        }
        break
      case 'time_activity[plan_id]':
        if (this.newValue) this.setFocusToDescription()
        break
    }
  }

  // Focus the first present field after the item: project, then plan, then
  // fall back to the description. A plan that came pre-selected is skipped —
  // it is already answered.
  advancePastItem() {
    this.focusAutocompleteById("time_activity_project_id_wrapper")
      || this.focusUnfilledAutocompleteById("time_activity_plan_id_wrapper")
      || this.setFocusToDescription()
  }

  async loadCustomerFields(customerId) {
    let url = "/time_activities/customer_fields"
    if (customerId) {
      url += `?customer_id=${customerId}`
    }

    const response = await fetch(url, {
      headers: { "Accept": "text/vnd.turbo-stream.html" }
    })
    const html = await response.text()
    Turbo.renderStreamMessage(html)

    // After Turbo replaces the DOM (new entries only): if the item was
    // auto-defaulted (General IT / MITS), skip past it to
    // project/plan/description; otherwise land on the item field so the user
    // can pick one. When editing, leave focus where it is.
    requestAnimationFrame(() => {
      if (!this.newValue) return
      const item = this.findAutocompleteOutletById("time_activity_item_id_wrapper")
      if (item && item.hiddenTarget.value) {
        this.advancePastItem()
      } else if (item) {
        item.setFocus()
      }
    })
  }
}
